import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Clock, ChevronRight, Calendar, Tag, Phone, ArrowLeft, Share2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
        // Update document title for SEO
        document.title = response.data.meta_title;
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Artículo no encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A365D] mx-auto"></div>
          <p className="mt-4 text-[#4A5568]">Cargando artículo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-[#1A365D] mb-4">Artículo no encontrado</h1>
          <p className="text-[#4A5568] mb-6">El artículo que buscas no existe o ha sido eliminado.</p>
          <Link
            to="/blog"
            className="btn-primary inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="industrial-bg" data-testid="blog-post-page">
      {/* Hero with Image */}
      <section className="relative bg-[#1A365D]">
        <div className="absolute inset-0 opacity-30">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D] to-[#1A365D]/70"></div>
        <div className="section-container py-20 relative z-10">
          <nav className="text-sm text-gray-300 mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 flex-wrap">
              <li><Link to="/" className="hover:text-white">Inicio</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-white truncate max-w-[200px]">{post.title}</li>
            </ol>
          </nav>
          
          <div className="flex items-center gap-4 text-sm text-gray-300 mb-4">
            <span className="flex items-center gap-1 bg-[#3182CE] text-white px-3 py-1 rounded-sm">
              <Tag className="w-4 h-4" />
              {post.category}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.read_time} min de lectura
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.published_date).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            {post.title}
          </h1>
          
          <p className="text-lg text-gray-300 max-w-3xl">
            {post.excerpt}
          </p>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <article className="lg:col-span-2">
              <div 
                className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-[#1A365D] prose-p:text-[#4A5568] prose-strong:text-[#1A365D] prose-a:text-[#3182CE] prose-li:text-[#4A5568] prose-table:text-sm"
                dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
                data-testid="blog-post-content"
              />
              
              {/* Share & Back */}
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-200">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#1A365D] transition-colors"
                  data-testid="back-to-blog"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Volver al Blog
                </Link>
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#3182CE] transition-colors"
                  data-testid="share-button"
                >
                  <Share2 className="w-5 h-5" />
                  Compartir
                </button>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              {/* CTA Card */}
              <div className="bg-[#1A365D] text-white p-8 rounded-sm mb-8 sticky top-40">
                <h3 className="font-heading text-xl font-bold uppercase mb-4">
                  ¿Necesitas Material?
                </h3>
                <p className="text-gray-300 text-sm mb-6">
                  En Fornes e Hijos tenemos todo lo que necesitas para tus proyectos 
                  de pladur y construcción en Motril.
                </p>
                <a
                  href="tel:858130213"
                  className="flex items-center justify-center gap-2 bg-white text-[#1A365D] font-semibold px-4 py-3 rounded-sm hover:bg-gray-100 transition-colors w-full mb-3"
                  data-testid="sidebar-cta-call"
                >
                  <Phone className="w-5 h-5" />
                  858 130 213
                </a>
                <Link
                  to="/productos"
                  className="flex items-center justify-center gap-2 border border-white text-white font-semibold px-4 py-3 rounded-sm hover:bg-white/10 transition-colors w-full"
                  data-testid="sidebar-cta-productos"
                >
                  Ver Productos
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>

              {/* Keywords */}
              <div className="bg-white p-6 rounded-sm border border-gray-200">
                <h4 className="font-heading text-lg font-bold text-[#1A365D] mb-4">
                  Temas Relacionados
                </h4>
                <div className="flex flex-wrap gap-2">
                  {post.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-[#4A5568] text-sm rounded-sm"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related CTA */}
      <section className="py-12 bg-gray-100">
        <div className="section-container text-center">
          <h2 className="font-heading text-2xl font-bold text-[#1A365D] uppercase mb-4">
            Tu Proveedor de Confianza en Motril
          </h2>
          <p className="text-[#4A5568] mb-6 max-w-xl mx-auto">
            Más de 30 años de experiencia suministrando materiales de construcción 
            a profesionales de la Costa Tropical.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/sobre-nosotros"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Conoce Nuestra Historia
            </Link>
            <Link
              to="/contacto"
              className="btn-primary inline-flex items-center gap-2"
            >
              Contactar
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Helper function to format markdown-like content to HTML
function formatContent(content) {
  if (!content) return '';
  
  let html = content
    // Headers
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^#### (.*$)/gm, '<h4>$1</h4>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^- (.*$)/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.*$)/gm, '<li>$2</li>')
    // Tables (basic support)
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      if (cells.every(c => c.trim().match(/^[-:]+$/))) {
        return ''; // Skip separator row
      }
      const isHeader = match.includes('---');
      const tag = isHeader ? 'th' : 'td';
      return `<tr>${cells.map(c => `<${tag} class="border border-gray-300 px-4 py-2">${c.trim()}</${tag}>`).join('')}</tr>`;
    })
    // Checkmarks
    .replace(/✅/g, '<span class="text-green-600">✅</span>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n(?!<)/g, '<br/>');

  // Wrap lists
  html = html.replace(/(<li>.*<\/li>)/gs, '<ul class="list-disc pl-6 my-4">$1</ul>');
  
  // Wrap in paragraphs
  html = `<p>${html}</p>`;
  
  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<p>\s*<h/g, '<h');
  html = html.replace(/<\/h(\d)>\s*<\/p>/g, '</h$1>');
  
  return html;
}

export default BlogPostPage;
